import { Navbar, Footer, GlassCard } from '../components/Layout';
import { Shield, Lock, Eye, Users, FileText, Server, CheckCircle } from 'lucide-react';

const securityFeatures = [
  {
    icon: Lock,
    title: 'Encryption at Rest',
    description: 'All your documents and personal data are encrypted using AES-256 encryption when stored in our systems.'
  },
  {
    icon: Shield,
    title: 'HTTPS in Transit',
    description: 'All data transmitted between your device and our servers is protected with TLS 1.3 encryption.'
  },
  {
    icon: Users,
    title: 'Role-Based Access',
    description: 'Strict access controls ensure only authorized users (students, parents, admins) can access appropriate data.'
  },
  {
    icon: Eye,
    title: 'Audit Logging',
    description: 'Every AI action and data access is logged with timestamps, providing full transparency.'
  },
  {
    icon: FileText,
    title: 'Secure Document Vault',
    description: 'Documents are stored with signed URLs, ensuring only authorized users can download files.'
  },
  {
    icon: Server,
    title: 'Data Isolation',
    description: 'Each user\'s data is logically isolated, preventing any cross-account access.'
  }
];

const parentControls = [
  'Approve or deny data sharing with colleges',
  'View student activity and progress',
  'Manage document visibility settings',
  'Control AI feature access',
  'Export student data anytime',
  'Delete account and all associated data'
];

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      
      <section className="pt-32 pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white mb-4">
              Security & Privacy
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Your data security is our top priority. We use industry-leading practices to protect student information.
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <GlassCard key={index}>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-300">{feature.description}</p>
                </GlassCard>
              );
            })}
          </div>

          {/* Parent Controls Section */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <GlassCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <h2 className="font-outfit text-xl font-semibold text-white">Parental Controls</h2>
              </div>
              <p className="text-slate-300 mb-6">
                Parents and guardians have full visibility and control over their student's data and account activity.
              </p>
              <ul className="space-y-3">
                {parentControls.map((control, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{control}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>

            <GlassCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-cyan-400" />
                </div>
                <h2 className="font-outfit text-xl font-semibold text-white">Document Vault Privacy</h2>
              </div>
              <p className="text-slate-300 mb-6">
                Your uploaded documents (transcripts, test scores, resumes) are protected with multiple layers of security.
              </p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  Files encrypted with AES-256 before storage
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  Signed URLs for secure, time-limited downloads
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  Documents never shared without explicit consent
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  AI analysis runs in isolated environments
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  Complete deletion upon account removal
                </li>
              </ul>
            </GlassCard>
          </div>

          {/* Compliance Section */}
          <GlassCard className="text-center max-w-4xl mx-auto">
            <h2 className="font-outfit text-2xl font-semibold text-white mb-4">Our Commitment</h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              We're committed to protecting student data and maintaining the highest standards of privacy and security.
              We follow FERPA guidelines and best practices for educational technology.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['FERPA Compliant', 'SOC 2 Ready', 'COPPA Aware', 'GDPR Ready'].map((badge, index) => (
                <div 
                  key={index}
                  className="px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700/50 text-slate-300"
                >
                  {badge}
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </section>

      <Footer />
    </div>
  );
}
